import { Job, SaveJobResponse, saveJobStatuses, SearchResults, Application, HiringPath } from "../../Shared/scriptts/interfaces";
import { getUserObject, setUserObject, saveJobSessionKey } from "../../Shared/scriptts/session/sessionmain";
import { params, signinClick } from '../search-main';
import { logSaveJob, logSearchConversion } from "../../Shared/scriptts/analytics";
import { getHiringPathColors, getHiringPathsSpriteUrl, getIconSvgTagFromSprite, getJobClockDisplay, getRequestVerificationToken, htmlEncode, isEmpty, loadAlertBanner } from "../../Shared/scriptts/utilities";
import { bindToggles } from "../../Shared/scriptts/rootcomponents/toggle";

declare var gMaximumSavedJobs: number;
declare var gMaximumSavedJobsReachedMessage: string;

const daysInMS = 86400000;
const today = new Date();

document.addEventListener("DOMContentLoaded", () => {
    const saveJobData = localStorage.getItem(saveJobSessionKey);
    if (saveJobData) {
        localStorage.removeItem(saveJobSessionKey);
        try {
            const {docId, save, joa} = JSON.parse(saveJobData);
            saveJob(docId, save, document.querySelector<HTMLButtonElement>(`[data-save-job="${docId}"]`), joa)
        } catch (error) {
            console.error("Failed to parse saveJobData from localStorage:", error);
        }
    }
});

function getAppliedHtml(job: Job, application: Application | null): string {
    if (!application) {
        return '';
    }

    if (application.DateSubmitted && application.DateSubmitted !== '') {
        return `<div class="flex items-center">${getIconSvgTagFromSprite('check', 'w-5 h-5 fill-success')}Applied ${new Date(application.DateSubmitted).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</div>`;
    }
    else if (application.Status === 'In Progress') {
        return `<div class="flex items-center gap-x-1">${getIconSvgTagFromSprite('warning', 'w-5 h-5 fill-secondary')}<a href="/applicant/application/applystart/${job.DocumentID}" aria-label="Finish application for ${getJobTitleContext(job)}" title="Finish application for ${getJobTitleContext(job)}">Finish application</a></div>`;
    }
    else {
        return '';
    }
}

function getJobTitleContext(job: Job): string {
    const parts = [job.Title, job.Agency, job.Department, getLocationsHtml(job)].filter(part => !isEmpty(part));
    return parts.join(' - ');
}

function getHiringPathBadge(hiringPath: HiringPath): string {
    const { backgroundColorClass, textColorClass } = getHiringPathColors(hiringPath.Code);
    return `<div class="badge ${backgroundColorClass} ${textColorClass} m-0 flex items-center gap-x-1"><svg class="w-4 h-4" style="--hiring-path-fill: transparent" role="img" aria-label="${hiringPath.Tooltip}"><title>${hiringPath.Tooltip}</title><use xlink:href="${getHiringPathsSpriteUrl()}#${hiringPath.Code}" /></svg>${hiringPath.SearchDisplay}</div>`;
}

function getLocationsHtml(job: Job): string {
    return job.LocationDisplay;
}

function getHiringPathHtml(job: Job): string {
    const hpLength = job.HiringPath.length;
    if (hpLength === 0) {
        return '';
    }

    const jobTitleContext = getJobTitleContext(job);

    let hpDisplay = `<div id="hp-1-${job.DocumentID}" class="flex gap-2" data-expanded="true">${getHiringPathBadge(job.HiringPath[0])}`;

    if (hpLength > 1) {
        hpDisplay += `<button class="button button-unstyled flex items-center" data-toggle data-target="hp-${job.DocumentID},hp-1-${job.DocumentID}" title="Show all hiring paths for ${jobTitleContext}">${getIconSvgTagFromSprite('add', 'w-4 h-4 fill-base pointer-events-none')} Show ${hpLength - 1} more</button></div>
            <div id="hp-${job.DocumentID}" class="flex flex-wrap gap-2 hidden">`;
        job.HiringPath.forEach(hp => hpDisplay += `${getHiringPathBadge(hp)}`);
        hpDisplay += `<button class="button button-unstyled flex items-center" data-toggle data-target="hp-${job.DocumentID},hp-1-${job.DocumentID}" title="Hide hiring paths for ${jobTitleContext}">${getIconSvgTagFromSprite('remove', 'w-4 h-4 fill-base pointer-events-none')} Show less</button></div>`;
    }
    else {
        hpDisplay += '</div>';
    }

    return hpDisplay;
}

function getJobBadgesHtml(job: Job): string {
    const badgeValues = [job.SalaryDisplay, job.WorkType, job.WorkSchedule].filter(value => !isEmpty(value));
    const badgesHTML = badgeValues.map(value => `<span class="badge badge-secondary mt-2 text-left whitespace-normal">${htmlEncode(value)}</span>`).join('');
    return badgesHTML.trim() === '' ? '' : `<div>${badgesHTML}</div>`;
}

/**
 * Builds search results cards and adds them to the results element.
 * @param data The data returned from the search api
 * @returns void
 */
export function buildSearchResultsCards(data: SearchResults) {
    const userObject = getUserObject();
    const resultsElem = document.querySelector<HTMLElement>('#search-results');
    if (!resultsElem) {
        return;
    }
    let resultsHtml = '';

    data.Jobs?.forEach((job, i) => {
        const isSaved = job.DocumentID in userObject.SavedJobs ? true : false;
        const appliedHtml = getAppliedHtml(job, userObject.AppliedJobs[job.DocumentID]);
        const saveJobButtonHtml = `<button class="flex whitespace-nowrap flex-nowrap items-center cursor-pointer text-sm gap-x-1" data-save-job="${job.DocumentID}" data-save="${!isSaved}">
               ${getIconSvgTagFromSprite((isSaved ? 'star' : 'star_outline'), 'w-4 h-4 fill-warning-dark')}
                ${isSaved ? "Saved to My jobs" : "Save job"}
            </button>`

        resultsHtml += `
            <div class="page-section">
                <div class="flex justify-end items-start gap-x-2 md:float-right">
                    ${!isEmpty(appliedHtml) ? `${appliedHtml} &bull;` : ''} ${saveJobButtonHtml}
                </div>
                <div>
                    <h2 class="font-normal text-base md:text-lg mr-2 mt-0"><a href="/job/${job.DocumentID}" data-search-result="${i}" data-document-id="${job.DocumentID}" class="no-underline">${job.Title}</a></h2>
                </div>
                <div class="grid md:grid-cols-2 md:gap-x-6">
                    <div>
                        <div><strong>${job.Agency}</strong> &bull; ${job.Department}</div>
                        <div class="flex items-center">${getIconSvgTagFromSprite('location_on', 'w-4 h-4 fill-gray-darkest')}${getLocationsHtml(job)}</div>
                        ${getJobClockDisplay(job, 'mt-2 italic text-gray-dark')}
                    </div>
                    <div class="mt-2 md:mt-0">${getHiringPathHtml(job)}${getJobBadgesHtml(job)}</div>
                </div>
            </div>`;
    });
    resultsElem.innerHTML = resultsHtml;
    document.querySelectorAll<HTMLElement>(`[data-search-result]`).forEach(elem => elem.addEventListener('click', evt => searchResultClicked(evt, data)));

    data.Jobs?.forEach(job => {
        const saveJobElem = document.querySelector<HTMLButtonElement>(`[data-save-job="${job.DocumentID}"]`);
        if (!saveJobElem) {
            return;
        }
        saveJobElem.addEventListener('click', evt => saveJobClicked(saveJobElem, evt, job));
    });

    bindToggles();
}

function searchResultClicked(evt: Event, results: SearchResults) {
    const elem = <HTMLElement>(evt.currentTarget);
    const index = elem.getAttribute('data-search-result') || '';
    const documentId = elem.getAttribute('data-document-id') || '';

    if (index === '' || documentId === '') {
        return;
    }

    const userObject = getUserObject();
    userObject.SearchResults = {
        Params: params,
        SelectedDocumentId: documentId,
        ConversionId: crypto.randomUUID(),
        Results: {
            Items: [],
            ResultCount: params.TotalResults || 0,
            ReturnUrl: window.location.href,
            PageSets: []
        }
    };
    
    userObject.SearchResults.Params.ResultsPerPage = results.Pager.ItemsPerPage;

    document.querySelectorAll<HTMLElement>('[data-search-result]').forEach(elem => {
        userObject.SearchResults.Results.Items.push(elem.getAttribute('data-document-id') || '');
    });
    userObject.SearchResults.Results.PageSets.push({
        Page: params.Page === undefined ? 1 : Number(params.Page),
        PageItems: userObject.SearchResults.Results.Items
    });
    
    setUserObject(userObject);
    logSearchConversion(userObject, "search_conversion_click");
}

function saveJobClicked(elem: HTMLButtonElement, event: Event, joa: Job) {
    const docId = Number(elem.getAttribute('data-save-job'));
    const save = elem.getAttribute('data-save');

    const userObject = getUserObject();
    if (save === 'true' && userObject.IsAuthenticated && hasReachedSavedJobsLimit(userObject)) {
        event.preventDefault();
        showSaveJobLimitAlert();
        return;
    }

    if (!userObject.IsAuthenticated) {
        localStorage.setItem(saveJobSessionKey, JSON.stringify({ docId, save, joa }));
        signinClick(event);
        return;
    } else {
        saveJob(docId, save, elem, joa);
    }
}

function saveJob(docId: number, save: string | null, elem: HTMLButtonElement | null, joa: Job) {
    if (save === null || isNaN(docId)) {
        return;
    };

    const userObject = getUserObject();
    if (save === 'true' && userObject.IsAuthenticated && hasReachedSavedJobsLimit(userObject)) {
        showSaveJobLimitAlert();
        return;
    }

    const requestVerificationToken = getRequestVerificationToken();

    fetch(`/search/savejob/${docId}/${save}`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
        },
        credentials: 'same-origin'
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Fetch error: ${response.status}`);
            }

            return response.json() as Promise<SaveJobResponse>;
        })
        .then(result => {
            if (result.Status === saveJobStatuses.maximumReached) {
                showSaveJobLimitAlert(result.Message);
                return;
            }

            var userObject = getUserObject();
            if (result.Status === saveJobStatuses.saved) {
                userObject.SavedJobs[docId] = true;
            }
            else if (result.Status === saveJobStatuses.deleted) {
                delete userObject.SavedJobs[docId];
                clearSearchMessages();
            }
            else {
                throw new Error(`Invalid save job status: ${result.Status}`);
            }

            updateSaveJobElem(elem, save === 'true' ? 'false' : 'true');
            setUserObject(userObject);
            logSaveJob(joa, (save === "true" ? true : false));
        })
        .catch(err => console.error(err));
}

function hasReachedSavedJobsLimit(userObject: ReturnType<typeof getUserObject>): boolean {
    const savedJobs = userObject.SavedJobs || {};
    return Object.keys(savedJobs).length >= gMaximumSavedJobs;
}

function showSaveJobLimitAlert(message: string = gMaximumSavedJobsReachedMessage) {
    const searchMessages = document.querySelector<HTMLElement>('#search-messages');
    if (!searchMessages) {
        return;
    }

    loadAlertBanner(searchMessages, { alertType: 'Error', message, isDismissable: true });
}

function clearSearchMessages() {
    const searchMessages = document.querySelector<HTMLElement>('#search-messages');
    if (!searchMessages) {
        return;
    }

    searchMessages.innerHTML = '';
}

function updateSaveJobElem(elem: HTMLButtonElement | null, save: string) {
    if (!elem) {
        return;
    }

    elem.setAttribute('data-save', save);
    elem.innerHTML = `${getIconSvgTagFromSprite(save === 'false' ? 'star' : 'star_outline', 'w-4 h-4 fill-warning-dark mr-1')} ${save === 'false' ? 'Saved to My jobs' : 'Save job'}`;
}
